import React from 'react';
import { useTheme } from '@mui/material';

import SimpleTooltip from '../../../../../components/SimpleTooltip';
import { ClaimIndicator } from '../../../../../_assets/svg/claims/ClaimIndicator.svg';

interface PolicyClaimIndicatorPolicy {
  readonly claimId?: string | null;
  readonly claimNumber?: string | null;
  readonly claimStatus?: string | null;
}

interface PolicyClaimIndicatorProps {
  readonly policy: PolicyClaimIndicatorPolicy;
}

/**
 * Persistent claims badge for the policy detail panel header (FIR-20830).
 *
 * Rendered once in PolicyDetailsDrawer's shared PolicyHeader, so it stays
 * visible across every tab (Policy Information, Organization Details,
 * Cowbell Factors) instead of being scoped to a single tab's content.
 *
 * Mirrors the row-level claim indicator already shown on the Cowbell
 * Policies listing (`policies/listing/cowbell/helpers.tsx` `PolicyNumberCell`)
 * so the same policy reads the same way in both places; the policy detail
 * API already serves `claimId`/`claimNumber`/`claimStatus` on the base
 * policy response (fir-account `BasePolicy`), so no backend change is
 * required here.
 */
export const PolicyClaimIndicator = ({ policy }: PolicyClaimIndicatorProps) => {
  const theme = useTheme();

  if (!policy?.claimId) {
    return null;
  }

  const claimStatus = policy.claimStatus ?? '';
  const tagColor =
    claimStatus === 'CLOSED' ? theme.config.colors.cowbellAccent : 'salmon';

  return (
    <SimpleTooltip
      title={`${claimStatus} - Claim Number ${policy.claimNumber}`}
    >
      <ClaimIndicator color={tagColor} status={claimStatus} />
    </SimpleTooltip>
  );
};
